import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Inbox, Search, Filter, ArrowRight, Eye, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { ENQUIRY_STATUS_CONFIG, EnquiryStatus } from '@ftt/shared';

export const AdminEnquiriesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

  const { data: enquiriesRes, isLoading } = useQuery({
    queryKey: ['admin-enquiries', { status: selectedStatus, search: searchTerm }],
    queryFn: () => {
      let url = '/enquiries';
      const params = new URLSearchParams();
      if (selectedStatus !== 'ALL') params.append('status', selectedStatus);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const enquiries = enquiriesRes || [];

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
            Publishing Enquiries &amp; Proposals
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Review incoming author submissions, assign acquisitions staff, and convert into active publishing projects.
          </p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-xl bg-slate-950 border border-slate-800">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ref no, title, author, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-brand-500"
          />
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          {['ALL', 'NEW', 'UNDER_REVIEW', 'CONSULTATION', 'PROPOSAL_SENT', 'CONVERTED', 'DECLINED'].map((st) => (
            <button
              key={st}
              onClick={() => setSelectedStatus(st)}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                selectedStatus === st
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-850'
              }`}
            >
              {st === 'ALL' ? 'All' : st.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Enquiries Table */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading enquiries...</div>
      ) : enquiries.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <Inbox className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Enquiries Found</h3>
          <p className="text-xs text-slate-400">No submissions match your current search and filter criteria.</p>
        </div>
      ) : (
        <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-900/80 text-slate-400 font-mono text-[11px] uppercase border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">Reference No</th>
                  <th className="px-6 py-4">Book Title &amp; Genre</th>
                  <th className="px-6 py-4">Author / Client</th>
                  <th className="px-6 py-4">Project Stage</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Submitted</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-sans">
                {enquiries.map((enq: any) => {
                  const statusConf = ENQUIRY_STATUS_CONFIG[enq.status as EnquiryStatus] || { label: enq.status, color: 'blue' };
                  return (
                    <tr key={enq.id} className="hover:bg-slate-900/50 transition-colors">
                      <td className="px-6 py-4 font-mono font-bold text-brand-400">
                        {enq.referenceNo}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-white">{enq.bookTitle}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{enq.genre}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-200">{enq.fullName}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{enq.email}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-400 font-mono text-[11px]">
                        {enq.stage.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded text-[10px] font-mono font-bold uppercase bg-slate-900 border border-slate-700 text-brand-300">
                          {statusConf.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-400 font-mono text-[11px]">
                        {new Date(enq.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/admin/enquiries/${enq.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-brand-600/20 hover:bg-brand-600 text-brand-300 hover:text-white font-bold text-xs transition-colors border border-brand-500/40"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Review</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};
